import { useState } from 'react';
import config from 'config';
import Image from 'next/image';

import {
  Flex,
  Grid,
  GridItem,
  Stack,
  Text,
  useBoolean,
  useTheme
} from '@chakra-ui/react';

import { formatDuration } from 'utils';

import { AppointmentService, ServiceCheckoutData } from 'types';

import { Divider, Loader } from 'ui';

const {
  booking: { DefaultProviderAvatar }
} = config;

interface ReviewItemProps {
  item: ServiceCheckoutData;
  initialServiceData: AppointmentService | undefined;
}

const ReviewItem = ({ item, initialServiceData }: ReviewItemProps) => {
  const [imageError, setImageError] = useState<boolean>(false);
  const [loading, setLoading] = useBoolean(true);
  const { total_duration, assigned_em, sv_name } = item;

  const {
    colors: {
      palette: {
        booking: {
          stepSuccess: {
            reviewList: { reviewItem }
          }
        }
      }
    }
  } = useTheme();

  const duration = formatDuration(total_duration);
  const providerDescription = assigned_em?.[0]?.assigned_em_title ?? '';
  const providerName = assigned_em?.[0]?.assigned_em_name ?? '';
  const providerAvatar = initialServiceData?.public_photo_url?.[0] || '';

  return (
    <Grid
      position='relative'
      templateColumns='repeat(12, 1fr)'
      templateRows={{
        base: 'repeat(3, auto)',
        lg: 'repeat(1, 1fr)'
      }}
      px={{ base: 15, md: '30px' }}
      py={{ base: '15px', md: '23px' }}
      gap={{ base: '10px', md: '20px' }}
      alignItems='center'
    >
      <GridItem colSpan={{ base: 7, lg: 6 }} gridRowStart={1}>
        <Text
          color={reviewItem.serviceNameColor}
          fontSize={{ base: '20px', md: '23px', xl: '25px' }}
          mt={{ base: '-3px', lg: 0 }}
        >
          {sv_name}
        </Text>
      </GridItem>
      <GridItem colSpan={{ base: 7, lg: 3 }} rowSpan={{ base: 2, lg: 1 }}>
        <Flex alignItems='center' gap='10px'>
          <Flex
            w={{ base: '35px', sm: '75px' }}
            h={{ base: '35px', sm: '75px' }}
            position='relative'
            overflow='hidden'
            bg={reviewItem.multipleProvidersBg}
            alignItems='center'
            justifyContent='center'
            borderRadius='50%'
            flexShrink={0}
          >
            {providerAvatar?.includes('http') && !imageError ? (
              <>
                {loading && <Loader absolute />}
                <Image
                  sizes='(max-width: 450px) 40px, 65px'
                  src={providerAvatar}
                  alt='Appointment'
                  fill
                  onError={() => setImageError(true)}
                  onLoad={setLoading.off}
                  style={{ objectFit: 'cover', objectPosition: 'top' }}
                />
              </>
            ) : (
              <DefaultProviderAvatar
                width={65}
                height={65}
                fill={reviewItem.iconFill}
                style={{ opacity: 0.5 }}
              />
            )}
          </Flex>

          <Stack gap='6px' justifyContent='center' height='100%'>
            <Text
              color={reviewItem.providerNameColor}
              fontSize={{ base: '11px', md: '12px' }}
              textTransform='uppercase'
            >
              {providerName}
            </Text>

            {providerDescription && (
              <Text
                color={reviewItem.providerDescriptionColor}
                fontSize={{ base: '10px', md: '12px' }}
              >
                {providerDescription}
              </Text>
            )}
          </Stack>
        </Flex>
      </GridItem>
      <GridItem
        colSpan={{ base: 5, lg: 3 }}
        rowSpan={{ base: 3, lg: 1 }}
        gridRowStart={1}
      >
        <Stack alignItems='flex-end' gap='0' ml='20px'>
          <Stack gap='8px' w='100%' alignItems='flex-end'>
            <Text fontSize={{ base: '20px', md: '23px', xl: '25px' }}>
              {duration}
            </Text>
          </Stack>
        </Stack>
      </GridItem>
      <Divider
        position='absolute'
        width='100%'
        border='none'
        bottom='-5px'
        left='0'
        h='1px'
        bg={reviewItem.dividerColor}
      />
    </Grid>
  );
};

export default ReviewItem;
